import type { Job } from '@/lib/api/types';
import { JobFailedError, pollJob, saveUrl, type PollJobOptions } from '@/lib/api/jobs';
import { dismissToast, toast, updateToast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';

const PROGRESS_TOAST_MS = 60 * 60_000;
const READY_TOAST_MS = 60_000;

/**
 * Bulk "Download zip": the API answers with a `job_id` and the client polls `GET /jobs/{jobId}` (members) or
 * `GET /public/share/{token}/jobs/{jobId}` (share guests). One toast tracks the job: queued → running N% → ready
 * with a Download action (`<a download>` on click, which keeps the gesture synchronous for Safari/Firefox).
 */
export async function trackZipJob(fetchJob: () => Promise<Job>, opts: { count?: number; filename?: string; signal?: AbortSignal; poll?: PollJobOptions } = {}): Promise<Job | null> {
  const what = opts.count ? `${opts.count} asset${opts.count === 1 ? '' : 's'}` : 'your selection';
  const id = toast({ kind: 'info', title: 'Preparing zip…', body: `Zipping ${what}. This can take a moment.`, durationMs: PROGRESS_TOAST_MS });
  try {
    const job = await pollJob(fetchJob, {
      ...opts.poll,
      signal: opts.signal,
      onProgress: (j) => updateToast(id, { title: j.state === 'running' ? `Zipping… ${Math.round(j.progress)}%` : 'Preparing zip…', body: j.state === 'queued' ? 'Waiting for a worker.' : `Zipping ${what}.` }),
    });
    const url = job.result?.url ?? null;
    dismissToast(id);
    if (!url) {
      toast({ kind: 'error', title: 'Zip finished without a download link' });
      return job;
    }
    toast({ kind: 'success', title: 'Zip ready', body: `${what} · link valid for 24 h`, durationMs: READY_TOAST_MS, action: { label: 'Download', onClick: () => saveUrl(url, opts.filename ?? 'assets.zip') } });
    return job;
  } catch (e) {
    dismissToast(id);
    if (e instanceof DOMException && e.name === 'AbortError') return null;
    toast({ kind: 'error', title: 'Zip failed', body: e instanceof JobFailedError ? e.message : isApiError(e) ? e.message : e instanceof Error ? e.message : undefined });
    return null;
  }
}
